import { useEffect, useRef, useState } from 'react'
import { Field, FIELD_CONTROL, FIELD_INVALID } from './Field'
import { Icon } from './Icon'
import { Popover } from './Popover'
import { cx } from '@/lib/cx'
import { formatClock, parseTimeInput, splitClock, toWireTime } from '@/lib/times'

interface Props {
  label: string
  /** `HH:MM`, 24-hour — the wire format, and what a list sorts by. */
  value: string
  onChange: (value: string) => void
  required?: boolean
  error?: string | undefined
  hint?: string | undefined
  disabled?: boolean
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)
/** Five-minute steps. A clinic books on the clock, not to the minute, and 60
 *  rows of minutes is a scroll nobody wants mid-call. */
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5)

/**
 * A time field that reads "5:30 pm" and stores "17:30".
 *
 * The stored value stays 24-hour: it is what a list sorts by and what the
 * server keeps. Only the reading and the typing are 12-hour, because a clinic
 * says "half five in the evening", and a field that shows 17:30 is one more
 * translation to do while somebody is on the phone.
 *
 * Typing still works and accepts either — "5:30pm", "17:30", "1730" — so the
 * fast path never depends on opening the picker.
 */
export function TimeField({
  label,
  value,
  onChange,
  required = false,
  error,
  hint,
  disabled = false,
}: Props) {
  const [text, setText] = useState(() => formatClock(value))
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const field = useRef<HTMLInputElement>(null)

  // The value can change under us — a form resetting, a draft restoring.
  useEffect(() => setText(formatClock(value)), [value])

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element
      if (wrap.current?.contains(target) || target.closest?.('[data-popover]')) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  const parts = splitClock(value)

  function pick(next: { hour?: number; minute?: number; meridiem?: 'am' | 'pm' }) {
    const base = parts ?? { hour: 9, minute: 0, meridiem: 'am' as const }
    const merged = { ...base, ...next }
    onChange(toWireTime(merged.hour, merged.minute, merged.meridiem))
  }

  return (
    <Field label={label} required={required} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <div className="relative" ref={wrap}>
          <input
            ref={field}
            id={id}
            type="text"
            autoComplete="off"
            placeholder="h:mm am"
            value={text}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            onChange={(event) => {
              setText(event.target.value)
              const wire = parseTimeInput(event.target.value)
              if (wire) onChange(wire)
              else if (event.target.value.trim() === '') onChange('')
            }}
            onBlur={() => {
              // A half-typed time is not a time: it is either valid or cleared.
              const wire = parseTimeInput(text)
              if (wire) {
                setText(formatClock(wire))
                onChange(wire)
              } else if (text.trim() === '') {
                onChange('')
              } else {
                setText(formatClock(value))
              }
            }}
            className={cx(FIELD_CONTROL, 'pr-touch tabular-nums', invalid && FIELD_INVALID)}
          />

          <button
            type="button"
            disabled={disabled}
            aria-label={open ? 'Close the time picker' : 'Choose a time'}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="absolute top-0 right-0 grid h-full w-touch place-items-center rounded-md text-fg-subtle transition-colors duration-150 ease-standard hover:text-fg disabled:text-fg-disabled"
          >
            <Icon name="calendarDays" size="1.125rem" />
          </button>

          <Popover anchor={field} open={open} align="start">
            <div
              role="dialog"
              aria-label="Choose a time"
              onKeyDown={(event) => {
                if (event.key !== 'Escape') return
                event.preventDefault()
                setOpen(false)
              }}
              className="flex w-64 gap-2 rounded-lg border border-line bg-raised p-2 shadow-lg"
            >
              <Column label="Hour">
                {HOURS.map((hour) => (
                  <Choice
                    key={hour}
                    selected={parts?.hour === hour}
                    onSelect={() => pick({ hour })}
                  >
                    {hour}
                  </Choice>
                ))}
              </Column>
              <Column label="Minute">
                {MINUTES.map((minute) => (
                  <Choice
                    key={minute}
                    selected={parts?.minute === minute}
                    onSelect={() => pick({ minute })}
                  >
                    {String(minute).padStart(2, '0')}
                  </Choice>
                ))}
              </Column>
              <Column label="am / pm">
                {(['am', 'pm'] as const).map((meridiem) => (
                  <Choice
                    key={meridiem}
                    selected={parts?.meridiem === meridiem}
                    onSelect={() => pick({ meridiem })}
                  >
                    {meridiem}
                  </Choice>
                ))}
              </Column>
            </div>
          </Popover>
        </div>
      )}
    </Field>
  )
}

function Column({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="px-1 pb-1 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
        {label}
      </span>
      <ul
        aria-label={label}
        className="m-0 max-h-52 list-none overflow-y-auto p-0 [scrollbar-width:thin]"
      >
        {children}
      </ul>
    </div>
  )
}

function Choice({
  selected,
  onSelect,
  children,
}: {
  selected: boolean
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={cx(
          'w-full rounded-sm px-2 py-2 text-center text-sm tabular-nums transition-colors duration-150 ease-standard',
          selected ? 'bg-accent font-semibold text-on-accent' : 'text-fg hover:bg-hover',
        )}
      >
        {children}
      </button>
    </li>
  )
}
